import { Identifier, MatchStatus } from '@okletsplay/core';
import { MATCH_ID_QUERY_KEY } from 'app/constants';
import { DialogName, ModalName } from 'app/enums';
import { UserEntity } from 'app/interfaces/entity';
import { ActivityDetectorContext, WindowFocusDetectorContext } from 'app/providers';
import { modalsReducers } from 'app/store/modal/slice';
import { sessionSelector } from 'app/store/session/selectors';
import { useMatchResult } from 'modules/matches/hooks/use-match-result';
import { currentMatchSelector } from 'modules/matches/store/current-match/selectors';
import { currentMatchReducers } from 'modules/matches/store/current-match/slice';
import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserWinningAnimation } from 'shared/hooks';

interface UseMatchModalHook {
  openMatchModal: () => void;
  closeMatchModal: () => void;
  setMatchIdQueryParam: (id: Identifier) => void;
  removeMatchIdQueryParam: () => void;
}

export const useMatchModal = (): UseMatchModalHook => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const match = useSelector(currentMatchSelector.match);
  const { focused } = useContext(WindowFocusDetectorContext);
  const { isUserActive } = useContext(ActivityDetectorContext);

  const { isCurrentPlayerWinner } = useMatchResult(match);
  const activeUser: Partial<UserEntity> = useSelector(sessionSelector.activeUser);
  const { isWinningAnimationAvailable } = useUserWinningAnimation(match, activeUser);

  const openMatchModal = useCallback(() => {
    if ([MatchStatus.Created, MatchStatus.Pending, MatchStatus.Declined].includes(match.status)) {
      dispatch(
        modalsReducers.closeModals([
          ModalName.MatchResult,
          ModalName.MatchLeaderboard,
          DialogName.WinningAnimation,
        ])
      );
      dispatch(modalsReducers.openModal({ name: ModalName.Match }));
    }

    if ([MatchStatus.Live, MatchStatus.Lobby].includes(match.status)) {
      dispatch(
        modalsReducers.closeModals([
          ModalName.Match,
          ModalName.MatchResult,
          DialogName.WinningAnimation,
        ])
      );
      dispatch(modalsReducers.openModal({ name: ModalName.MatchLeaderboard }));
    }

    if (
      focused &&
      isUserActive &&
      isCurrentPlayerWinner &&
      isWinningAnimationAvailable &&
      match.status === MatchStatus.Finished
    ) {
      dispatch(
        modalsReducers.closeModals([
          ModalName.Match,
          ModalName.MatchResult,
          ModalName.MatchLeaderboard,
        ])
      );
      return dispatch(modalsReducers.openModal({ name: DialogName.WinningAnimation }));
    }

    if (
      [
        MatchStatus.Finished,
        MatchStatus.Aborted,
        MatchStatus.Expired,
        MatchStatus.Canceled,
      ].includes(match.status) &&
      focused
    ) {
      dispatch(
        modalsReducers.closeModals([
          ModalName.Match,
          ModalName.MatchLeaderboard,
          DialogName.WinningAnimation,
        ])
      );
      dispatch(modalsReducers.openModal({ name: ModalName.MatchResult }));
    }
  }, [
    match.status,
    focused,
    isUserActive,
    isCurrentPlayerWinner,
    isWinningAnimationAvailable,
    dispatch,
  ]);

  const closeMatchModal = useCallback(() => {
    dispatch(currentMatchReducers.clearMatch());

    dispatch(
      modalsReducers.closeModals([
        ModalName.Match,
        ModalName.MatchLeaderboard,
        ModalName.MatchResult,
        DialogName.WinningAnimation,
      ])
    );

    navigate(pathname);
  }, [dispatch, navigate, pathname]);

  const setMatchIdQueryParam = useCallback(
    (identifier: string) => {
      navigate({
        pathname: pathname,
        search: `?${MATCH_ID_QUERY_KEY}=${identifier}`,
      });
    },
    [navigate, pathname]
  );

  const removeMatchIdQueryParam = useCallback(() => navigate(pathname), [navigate, pathname]);

  return {
    openMatchModal,
    closeMatchModal,
    setMatchIdQueryParam,
    removeMatchIdQueryParam,
  };
};
